import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { BulkResetViewsButton } from './BulkDeleteUser';
import {
    Edit,
    SimpleForm,
    TextInput,
    Toolbar,
    SaveButton,
    Button,
    required,
    useRedirect,
    useRecordContext,
} from 'react-admin';
import { Typography, Chip } from '@material-ui/core/';
import { useLayer7Notify } from '../useLayer7Notify';
import { RoleSelectInput } from './RoleSelectInput';
import { CRUD_UPDATE, useRefresh, useTranslate, useUpdate } from 'ra-core';

const useUserUpdate = () => {
    const [update, state] = useUpdate('users');

    const userupdate = (data, { onSuccess, onFailure }) => {
        update(
            {
                payload: { data: data, id: data.id },
            },
            {
                action: CRUD_UPDATE,
                onSuccess: () => {
                    onSuccess();
                },
                onFailure: error => {
                    onFailure(error);
                },
            }
        );
    };

    return [userupdate, state];
};

const EditToolbar = props => {
    const redirect = useRedirect();
    const classes = useStyles(props);
    return (
        <Toolbar {...props}>
            <Button
                label="Cancel"
                className={classes.cancelButton}
                onClick={() => redirect('/users')}
            />
            <SaveButton {...props} className={classes.saveButton} />
        </Toolbar>
    );
};

const ActionsToolbar = props => {
    const record = useRecordContext();
    const history = useHistory();
    const classes = useStyles(props);
    const badgeColor =
        record?.status?.toLowerCase() === 'enabled' ? 'primary' : 'default';
    const buttonLabel =
        record?.status?.toLowerCase() === 'enabled' ? 'Disable' : 'Enable';
    const [userupdate] = useUserUpdate();
    const notify = useLayer7Notify();
    const translate = useTranslate();
    const statusTranslationKey = `resources.users.status.${record?.status?.toLowerCase()}`;
    const statusMapping = {
        ENABLED: 'DISABLED',
        DISABLED: 'ENABLED',
    };
    const { APIHUB_URL, TENANT_NAME, ORIGIN_HUB_NAME } = global.APIHUB_CONFIG;
    const resendEmail = async () => {
        const url =
            APIHUB_URL +
            '/api/' +
            TENANT_NAME +
            '/accountSetup/' +
            record.id +
            '/resendEmail';
        const response = await fetch(url, {
            headers: {
                'Content-Type': 'application/json;charset=UTF-8',
                'origin-hub-name': ORIGIN_HUB_NAME,
            },
            method: 'POST',
            credentials: 'include',
            body: '{}',
            Accept: 'application/json',
        });
        if (response.status === 200) {
            notify(
                'resources.users.notifications.resend_email_success',
                'info',
                {
                    smart_count: 1,
                }
            );
        } else {
            console.error(response.status + ':' + response.statusText);
            notify(
                'resources.users.notifications.resend_email_error',
                'error',
                {
                    smart_count: 1,
                }
            );
        }
    };

    const onStatusChange = () => {
        const payload = { ...record };
        payload.status = statusMapping[record.status];
        userupdate(payload, {
            onSuccess: () => {
                notify('resources.users.notifications.update_success', 'info', {
                    smart_count: 1,
                });
                //refresh();
                history.push('/users');
            },
            onFailure: error => {},
        });
    };

    return (
        <div className={classes.toolBar}>
            <Chip label={translate(statusTranslationKey)} color={badgeColor} />
            {record?.status?.toLowerCase() !== 'registration_init' && (
                <Button label={buttonLabel} onClick={onStatusChange} />
            )}
            {record?.status?.toLowerCase() === 'registration_init' && (
                <Button label="Resend email" onClick={resendEmail} />
            )}
            <BulkResetViewsButton {...props} />
        </div>
    );
};

const UserEdit = props => {
    const [selectedRole, setSelectedRole] = useState(null);
    const classes = useStyles(props);
    const translate = useTranslate();

    const propFromSelectedRole = item => {
        setSelectedRole(item);
    };

    return (
        <div className={classes.container}>
            <div className={classes.columns}>
                {' '}
                <Typography
                    variant="h4"
                    color="textPrimary"
                    className={classes.title}
                >
                    {translate('resources.users.editUser')}
                </Typography>
                <Edit mutationMode="pessimistic" {...props} resource="users">
                    <SimpleForm toolbar={<EditToolbar />}>
                        <ActionsToolbar />
                        <TextInput
                            disabled
                            label="Username"
                            fullWidth
                            source="username"
                        />
                        <TextInput
                            source="firstName"
                            fullWidth
                            validate={required()}
                        />
                        <TextInput
                            source="lastName"
                            fullWidth
                            validate={required()}
                        />
                        <TextInput
                            source="email"
                            fullWidth
                            validate={required()}
                        />
                        <RoleSelectInput
                            source="roleUuid"
                            onChange={propFromSelectedRole}
                        />
                    </SimpleForm>
                </Edit>
            </div>
        </div>
    );
};

const useStyles = makeStyles(theme => ({
    title: {
        marginBottom: theme.spacing(6),
    },
    toolBar: {
        marginBottom: '10px',
    },
    saveButton: {
        marginLeft: 'auto',
    },
    deleteButton: {
        float: 'right',
    },
    cancelButton: {
        color: 'rgba(0, 0, 0, 0.87)',
        padding: '10px 16px',
        background: '#ffffff',
        borderRadius: '2px',
    },
    container: {
        display: 'flex',
        flexWrap: 'wrap',
        flexGrow: 1,
        justifyContent: 'center',
    },
    columns: {
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(3),
        },
        '&:first-child': {
            minWidth: '580px',
            maxWidth: '30%',
            [theme.breakpoints.down('md')]: {
                maxWidth: '37%',
            },
            [theme.breakpoints.down('sm')]: {
                maxWidth: '100%',
                width: '100%',
            },
        },
    },
    errorIcon: {
        '& ion-icon': {
            color: '#FFCB00',
            height: '35px',
            width: '35px',
        },
    },
    confirmation: {
        marginTop: '15px',
    },
}));

export { UserEdit };
